/**
 * LRU cache for signed file URLs, backed by localStorage via local-cache.ts.
 *
 * Callers pass their own TTL (the right value depends on the bucket's signed
 * URL expiry). Chat message attachments use 6.5d, just under their 7d URL
 * expiry — see useChatImageSignedUrl. Shorter-lived buckets (e.g., 1h URLs)
 * still pass a matching shorter TTL. Budget: ~50KB in localStorage.
 */

import { getCacheSection, setCacheSection } from './local-cache';

interface CachedEntry {
  url: string;
  expiresAt: number;
  accessedAt: number;
}

type CacheStore = Record<string, CachedEntry>;

const SECTION = 'alfie-file-urls';
const MAX_ENTRIES = 100;

function loadStore(): CacheStore {
  return (getCacheSection<CacheStore>(SECTION) ?? {});
}

function saveStore(store: CacheStore): void {
  setCacheSection(SECTION, store);
}

/** Get a cached signed URL. Returns null if missing or expired. */
export function getCachedFileUrl(fileId: string): string | null {
  const store = loadStore();
  const entry = store[fileId];
  if (!entry) return null;

  if (Date.now() > entry.expiresAt) {
    delete store[fileId];
    saveStore(store);
    return null;
  }

  // Update access time for LRU
  entry.accessedAt = Date.now();
  saveStore(store);
  return entry.url;
}

/** Cache a signed URL with a TTL in milliseconds. */
export function setCachedFileUrl(fileId: string, url: string, ttlMs: number): void {
  const store = loadStore();

  store[fileId] = {
    url,
    expiresAt: Date.now() + ttlMs,
    accessedAt: Date.now(),
  };

  // Evict expired entries first
  const now = Date.now();
  for (const key of Object.keys(store)) {
    if (now > store[key].expiresAt) {
      delete store[key];
    }
  }

  // LRU eviction if still over capacity
  const keys = Object.keys(store);
  if (keys.length > MAX_ENTRIES) {
    const sorted = keys.sort((a, b) => store[a].accessedAt - store[b].accessedAt);
    const toRemove = sorted.slice(0, keys.length - MAX_ENTRIES);
    for (const key of toRemove) {
      delete store[key];
    }
  }

  saveStore(store);
}

/** Remove all expired entries. Call periodically or on app init. */
export function evictExpired(): void {
  const store = loadStore();
  const now = Date.now();
  let changed = false;

  for (const key of Object.keys(store)) {
    if (now > store[key].expiresAt) {
      delete store[key];
      changed = true;
    }
  }

  if (changed) saveStore(store);
}
